import Component from '@glimmer/component';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import { eq, gt } from 'tio-ui/utilities';
import {
  PhTrendUp,
  PhTrendDown,
  PhChartBar,
  PhGraduationCap,
  PhUsers,
  PhTarget,
  PhArrowUp,
  PhArrowDown,
  PhLightbulb,
} from 'ember-phosphor-icons';

// Mock program effectiveness data
const PROGRAM_DATA = {
  summary: {
    totalPrograms: 156,
    activeEnrollments: 892,
    completionRate: 78,
    avgROI: 185,
    totalSpend: 1845000,
    promotionsFromTA: 67,
  },
  topPerformers: [
    {
      name: 'RN to BSN Bridge Program',
      provider: 'University Partners Network',
      enrolled: 156,
      completed: 134,
      avgTime: '18 months',
      promotionRate: 28,
      roi: 245,
      trend: 'up',
    },
    {
      name: 'Epic EHR Certification',
      provider: 'Epic Systems',
      enrolled: 89,
      completed: 82,
      avgTime: '3 months',
      promotionRate: 31,
      roi: 312,
      trend: 'up',
    },
    {
      name: 'Leadership Essentials Certificate',
      provider: 'Healthcare Leadership Institute',
      enrolled: 67,
      completed: 58,
      avgTime: '6 months',
      promotionRate: 24,
      roi: 198,
      trend: 'stable',
    },
    {
      name: 'Clinical Data Analytics',
      provider: 'Health Informatics Academy',
      enrolled: 34,
      completed: 28,
      avgTime: '12 months',
      promotionRate: 18,
      roi: 156,
      trend: 'up',
    },
  ],
  underperformers: [
    {
      name: 'General Business Admin',
      enrolled: 45,
      completed: 12,
      completionRate: 27,
      roi: 45,
      issue: 'Low completion',
    },
    {
      name: 'Basic Computer Skills',
      enrolled: 78,
      completed: 65,
      completionRate: 83,
      roi: 32,
      issue: 'Low ROI',
    },
    {
      name: 'Project Management Basics',
      enrolled: 34,
      completed: 8,
      completionRate: 24,
      roi: 67,
      issue: 'Low completion',
    },
  ],
  categoryROI: [
    { category: 'Clinical Certifications', spend: 425000, enrolled: 234, roi: 278, trend: 'up' },
    { category: 'Degree Programs', spend: 680000, enrolled: 156, roi: 165, trend: 'stable' },
    { category: 'Leadership Development', spend: 312000, enrolled: 189, roi: 198, trend: 'up' },
    { category: 'Technical Skills', spend: 245000, enrolled: 178, roi: 142, trend: 'down' },
    {
      category: 'Professional Certifications',
      spend: 183000,
      enrolled: 135,
      roi: 156,
      trend: 'stable',
    },
  ],
};

export default class CareerPathwaysAdminProgramEffectiveness extends Component {
  get data() {
    return PROGRAM_DATA;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12">
      {{! Header }}
      <div class="flex justify-between items-center mb-6">
        <div>
          <Heading @level={{1}} class="app-page-title">
            Program Effectiveness
          </Heading>
          <Text @variant="muted">ROI analysis and program performance tracking</Text>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined" @intent="primary">
            <PhChartBar @size={{18}} class="mr-2" />
            Export Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Configure Programs
          </Button>
        </div>
      </div>

      {{! Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Total Programs</Text>
          <div
            class="text-2xl font-bold tio-fg-primary mt-1"
          >{{this.data.summary.totalPrograms}}</div>
        </div>
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Active Enrollments</Text>
          <div
            class="text-2xl font-bold tio-fg-brand dark:text-violet-400 mt-1"
          >{{this.data.summary.activeEnrollments}}</div>
        </div>
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Completion Rate</Text>
          <div
            class="text-2xl font-bold tio-fg-success dark:text-green-400 mt-1"
          >{{this.data.summary.completionRate}}%</div>
        </div>
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Average ROI</Text>
          <div
            class="text-2xl font-bold text-teal-600 dark:text-teal-400 mt-1"
          >{{this.data.summary.avgROI}}%</div>
        </div>
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Total TA Spend</Text>
          <div class="text-2xl font-bold tio-fg-primary mt-1">$1.85M</div>
        </div>
        <div class="bg-white rounded-lg p-5 border tio-border-default">
          <Text @variant="muted" @size="sm">Promotions (TA)</Text>
          <div
            class="text-2xl font-bold tio-fg-warning dark:text-amber-400 mt-1"
          >{{this.data.summary.promotionsFromTA}}</div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Top Performers }}
        <div class="lg:col-span-2">
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhTrendUp @size={{20}} class="tio-fg-success" />
              <span class="font-medium tio-fg-primary">Top Performing Programs</span>
            </div>
            <div class="p-5">
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead>
                    <tr class="text-left text-sm tio-fg-muted border-b tio-border-default">
                      <th class="pb-3 font-medium">Program</th>
                      <th class="pb-3 font-medium text-center">Enrolled</th>
                      <th class="pb-3 font-medium text-center">Completed</th>
                      <th class="pb-3 font-medium text-center">Promotion Rate</th>
                      <th class="pb-3 font-medium text-right">ROI</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    {{#each this.data.topPerformers as |program|}}
                      <tr>
                        <td class="py-4">
                          <Text class="font-medium tio-fg-primary">{{program.name}}</Text>
                          <Text @variant="muted" @size="sm">{{program.provider}}</Text>
                        </td>
                        <td class="py-4 text-center tio-fg-secondary">{{program.enrolled}}</td>
                        <td class="py-4 text-center tio-fg-secondary">{{program.completed}}</td>
                        <td class="py-4 text-center">
                          <Chip @intent="success" @size="sm">{{program.promotionRate}}%</Chip>
                        </td>
                        <td class="py-4 text-right">
                          <div class="tio-row-extra-tight justify-end">
                            <span
                              class="font-semibold tio-fg-success dark:text-green-400"
                            >{{program.roi}}%</span>
                            {{#if (eq program.trend "up")}}
                              <PhArrowUp @size={{14}} class="tio-fg-success" />
                            {{else if (eq program.trend "down")}}
                              <PhArrowDown @size={{14}} class="tio-fg-danger" />
                            {{/if}}
                          </div>
                        </td>
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>

        {{! Underperformers }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhTrendDown @size={{20}} class="tio-fg-danger" />
            <span class="font-medium tio-fg-primary">Needs Attention</span>
          </div>
          <div class="p-5">
            <div class="space-y-4">
              {{#each this.data.underperformers as |program|}}
                <div
                  class="p-4 tio-bg-danger-soft dark:bg-red-900/30 rounded-lg border border-red-100 dark:border-red-800"
                >
                  <div class="flex items-center justify-between mb-2">
                    <Text class="font-semibold tio-fg-primary">{{program.name}}</Text>
                    <Chip @intent="danger" @size="sm">{{program.issue}}</Chip>
                  </div>
                  <div class="tio-row-loose text-sm">
                    <span class="tio-fg-secondary">Completion:
                      {{program.completionRate}}%</span>
                    <span class="tio-fg-secondary">ROI: {{program.roi}}%</span>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        </Card>
      </div>

      {{! Category ROI }}
      <Card @padding="none" class="mt-6">
        <div class="tio-row-tight p-5 border-b tio-border-subtle">
          <PhGraduationCap @size={{20}} class="tio-fg-brand" />
          <span class="font-medium tio-fg-primary">ROI by Program Category</span>
        </div>
        <div class="p-5">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="text-left text-sm tio-fg-muted border-b tio-border-default">
                  <th class="pb-3 font-medium">Category</th>
                  <th class="pb-3 font-medium text-center">Total Spend</th>
                  <th class="pb-3 font-medium text-center">Enrollments</th>
                  <th class="pb-3 font-medium text-center">ROI</th>
                  <th class="pb-3 font-medium text-center">Trend</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                {{#each this.data.categoryROI as |cat|}}
                  <tr>
                    <td class="py-4">
                      <Text class="font-medium tio-fg-primary">{{cat.category}}</Text>
                    </td>
                    <td class="py-4 text-center tio-fg-secondary">
                      ${{cat.spend}}
                    </td>
                    <td class="py-4 text-center tio-fg-secondary">{{cat.enrolled}}</td>
                    <td class="py-4 text-center">
                      {{#if (gt cat.roi 200)}}
                        <Chip @intent="success" @size="sm">{{cat.roi}}%</Chip>
                      {{else if (gt cat.roi 150)}}
                        <Chip @intent="warning" @size="sm">{{cat.roi}}%</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">{{cat.roi}}%</Chip>
                      {{/if}}
                    </td>
                    <td class="py-4 text-center">
                      {{#if (eq cat.trend "up")}}
                        <PhArrowUp @size={{18}} class="tio-fg-success mx-auto" />
                      {{else if (eq cat.trend "down")}}
                        <PhArrowDown @size={{18}} class="tio-fg-danger mx-auto" />
                      {{else}}
                        <span class="tio-fg-disabled">-</span>
                      {{/if}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {{! Insights }}
      <div
        class="mt-6 p-6 tio-bg-brand-wash dark:bg-violet-900/30 rounded-lg border tio-border-brand-soft dark:border-violet-700"
      >
        <Heading @level={{3}} class="text-lg font-semibold tio-fg-brand dark:text-violet-200 mb-4">
          <PhLightbulb @size={{20}} class="inline mr-2" />
          Key Insights
        </Heading>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="tio-row-top">
            <PhUsers @size={{20}} class="tio-fg-brand dark:text-violet-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Clinical certifications deliver the highest ROI at
              278%, consider expanding these offerings.</Text>
          </div>
          <div class="tio-row-top">
            <PhTrendDown @size={{20}} class="tio-fg-danger dark:text-red-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">General Business Admin has only 27% completion rate.
              Consider redesigning or replacing.</Text>
          </div>
          <div class="tio-row-top">
            <PhTrendUp @size={{20}} class="tio-fg-success dark:text-green-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Epic certification shows 31% promotion rate - highest
              career mobility impact.</Text>
          </div>
          <div class="tio-row-top">
            <PhTarget @size={{20}} class="tio-fg-warning dark:text-amber-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Technical skills category ROI declining. Review
              relevance to current role needs.</Text>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
